import React, { useState, useRef, type FC, useEffect } from 'react';

import {
  Drawer,
  Form,
  PhoneInputField,
  Select,
  Container,
  Row,
  Col,
  Input,
  Typography,
  toast,
  getStoreValue,
  EMAIL_REGEX,
  checkEmpty,
  ALPHABET_WITH_SPACES_ONLY_REGEX,
  validateFileContent,
} from 'pixel-react';
import type { UseFormSetValue } from 'react-hook-form';
import useGeoLocation from 'react-ipgeolocation';
import { useSelector } from 'react-redux';

import { PHONE_INPUT_STYLE } from '@/config/constants';
import { jobTitleOptions } from '@/constants/userProfile';
import { validateImageFile } from '@/lib/utils/commonUtils';
import { RootState } from '@/store';
import type {
  responseType,
  Test_Optimize_ResponseObject,
} from '@/store/commonApiType/testOptimizeUserType';
import {
  useLazyGetIndividualUsersQuery,
  useLazyGetProjectManagementQuery,
} from '@/store/projectManagement/usersManagement/projectManagementSlice';
import {
  useLazyGetUserProfileDetailsQuery,
  useUpdateProfileMutation,
} from '@/store/userProfile/userProfileSlice';

import OtpVerification from './OtpVerification';
import styles from './Profile.module.scss';
import ProfileImage from './ProfileImage';
import type { ProfileFormData, ProfileFormProps } from './type';

const ProfileForm: FC<ProfileFormProps> = ({ onClose }) => {
  const [getAllUsers] = useLazyGetProjectManagementQuery();
  const [getIndividualUsers] = useLazyGetIndividualUsersQuery();
  const [isDisabled, setIsDisabled] = useState(true);
  const [hasSecondaryPhone, setHasSecondaryPhone] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [editImageClicked, setEditImageClicked] = useState<boolean>(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState<boolean>(false);
  const projectDetails = getStoreValue('current-project') as { id: string };
  const [countryCode, setCountryCode] = useState<string>('IN');
  const [profileImageExists, setProfileImageExists] = useState<boolean>(false);
  const [isPhoneValid, setIsPhoneValid] = useState<boolean>(true);
  const [isSecondaryPhoneValid, setIsSecondaryPhoneValid] = useState(true);
  const location = useGeoLocation();
  const selectRef = useRef(null);
  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();
  const username = getStoreValue(
    'test-optimize-user',
  ) as Test_Optimize_ResponseObject;
  const userDetails = username;
  const userResponseData = userDetails.responseObject;
  const [userProfileDataRefetch] = useLazyGetUserProfileDetailsQuery();
  const profileData = useSelector(
    (state: RootState) => state.profileDataSlice.profileData,
  );
  const userData = profileData?.responseObject || {};
  const {
    name = '',
    emailId = '',
    phoneNumbers = {},
    organisationName = '',
    jobTitle = '',
    jobTitleOthers = '',
    verified,
  } = userData;
  const [phoneVerified, setPhoneVerified] = useState(verified);
  const [imageValidationError, setImageValidationError] = useState<
    string | null
  >(null);
  useEffect(() => {
    const { profileImage, profileImagePath } =
      profileData?.responseObject ?? '';
    const secondaryPhone =
      profileData?.responseObject?.phoneNumbers?.secondaryPhone;

    const isValidSecondaryPhone =
      !['---', '91', '+91'].includes(secondaryPhone) &&
      !checkEmpty(secondaryPhone);
    if (profileImage) {
      if (profileImagePath?.includes('svg')) {
        setImagePreview(`data:image/svg+xml;base64,${profileImage}`);
      } else {
        setImagePreview(`data:image;base64,${profileImage}`);
      }
      setProfileImageExists(true);
    }
    if (isValidSecondaryPhone) {
      setHasSecondaryPhone(true);
    }
  }, [profileData]);
  useEffect(() => {
    if (location?.country) {
      setCountryCode(location?.country);
    }
  }, [location?.country]);

  const handleSubmit = async (data: ProfileFormData) => {
    const secondaryPhoneNumber = ['+91', '91', '---']?.includes(
      data.secondaryPhone,
    )
      ? ''
      : data.secondaryPhone;
    try {
      const formPayload = {
        jobTitle: data.jobTitle,
        jobTitleOthers: data.otherJobTitle,
        organisationName: data.organisation,
        phoneNumbers: {
          primaryPhone: data.phone,
          secondaryPhone: secondaryPhoneNumber,
        },
        // verified: phoneVerified, // future use case
        profileImageExists: profileImageExists,
      };

      const formData = new FormData();
      formData.append('userData', JSON.stringify(formPayload));
      formData.append('profileImage', selectedFile as File);

      const response = await updateProfile({
        formPayload: formData,
        userId: userResponseData?.id ?? '',
      }).unwrap();
      if (response?.responseCode === 200) {
        userProfileDataRefetch(userResponseData?.id);
        if (projectDetails?.id === 'All Projects') {
          getAllUsers();
        } else {
          getIndividualUsers({ projectId: projectDetails?.id });
        }
        toast.success(response?.message ?? '');
      }

      onClose(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  const handleImageUpload = () => {
    fileInputRef.current?.click();
    setEditImageClicked(false);
  };

  const handleAddPhone = () => {
    setHasSecondaryPhone(true);
  };

  const handleRemovePhone = (setValue: UseFormSetValue<ProfileFormData>) => {
    setHasSecondaryPhone(false);
    setValue('secondaryPhone', '');
    setIsDisabled(false);
  };

  const cancelEditModal = () => {
    onClose(false);
  };

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const supportedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    const isValid = await validateImageFile(file);
    if (!supportedTypes.includes(file.type) || !isValid) {
      toast.error('Invalid file type. Please upload JPEG/JPG or PNG.');
      if (fileInputRef?.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    const maxSizeInMB = 2;
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      toast.error('File size exceeds the maximum limit of 2 MB.');
      if (fileInputRef?.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const isValidFile = await validateFileContent(file);
    if (!isValidFile) {
      toast.error(`${file.name} is corrupted or invalid.`);
      return;
    }
    setSelectedFile(file);
    setIsDisabled(false);
    setProfileImageExists(true);
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
    setImageValidationError(null);
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setImageValidationError('');
    setSelectedFile(null);
    setImagePreview(null);
    setIsDisabled(false);
    setProfileImageExists(false);
    setEditImageClicked(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleEditProfileImage = () => {
    setEditImageClicked(!editImageClicked);
  };
  const handlePhoneNumberValue = (value: string) => {
    if (value === '---') {
      return '';
    } else {
      return value;
    }
  };
  return (
    <Form
      onSubmit={handleSubmit}
      defaultValues={{
        name,
        email: emailId,
        phone: handlePhoneNumberValue(phoneNumbers?.primaryPhone),
        secondaryPhone: handlePhoneNumberValue(phoneNumbers?.secondaryPhone),
        organisation: organisationName,
        jobTitle,
        otherJobTitle: jobTitleOthers,
      }}
      reValidateMode="onChange"
      id="profile-form"
    >
      {({ register, setValue, watch, trigger, formState: { errors } }) => {
        const nameValue = watch('name');
        const emailValue = watch('email');
        const phoneValue = watch('phone');
        const secondaryPhoneValue = watch('secondaryPhone');
        const organizationValue = watch('organisation');
        const isBothNumbersSame = watch('phone') === watch('secondaryPhone');
        const isUpdateButtonDisabled =
          isDisabled ||
          isUpdating ||
          Object.keys(errors).length > 0 ||
          !isPhoneValid ||
          (hasSecondaryPhone && !isSecondaryPhoneValid);
        return (
          <Drawer
            isOpen
            title="User Profile"
            overlay
            top="56px"
            isBackButtonVisible={false}
            isFooterRequired
            onClose={cancelEditModal}
            primaryButtonProps={{
              label: 'Update',
              form: 'profile-form',
              type: 'submit' as const,
              disabled: isUpdateButtonDisabled,
            }}
            secondaryButtonProps={{
              label: 'Cancel',
              onClick: cancelEditModal,
            }}
            size="small"
          >
            <div className={styles.drawer_body}>
              <Container gap="24px" fluid>
                <Row>
                  <Col size={12}>
                    <div className={styles.profile_section}>
                      <div className={styles.avatar_container}>
                        <ProfileImage
                          imagePreview={imagePreview}
                          handleEditProfileImage={handleEditProfileImage}
                          handleImageUpload={handleImageUpload}
                          editImageClicked={editImageClicked}
                          handleRemoveFile={handleRemoveFile}
                        />
                        <input
                          type="file"
                          ref={fileInputRef}
                          accept=".png,.jpg,.jpeg"
                          onChange={handleImageChange}
                          style={{ display: 'none' }}
                        />
                      </div>
                      {imageValidationError && (
                        <Typography
                          color="var(--input-error-text-color)"
                          lineHeight="15px"
                        >
                          {imageValidationError}
                        </Typography>
                      )}
                    </div>
                  </Col>
                </Row>
                <Row gap="8px">
                  <Col size={6}>
                    <Input
                      label="Name"
                      required
                      placeholder="Enter Name"
                      type="text"
                      value={nameValue}
                      {...register('name', { required: 'Name is required' })}
                      onChange={e => {
                        setValue('name', e.target.value, {
                          shouldValidate: true,
                        });
                        setIsDisabled(false);
                      }}
                      error={!!errors.name}
                      disabled
                    />
                  </Col>
                  <Col size={6}>
                    <Input
                      label="Email"
                      required
                      placeholder="Enter Email"
                      type="email"
                      value={emailValue}
                      {...register('email', {
                        pattern: {
                          value: EMAIL_REGEX,
                          message: 'Invalid email address',
                        },
                      })}
                      onChange={e => {
                        setValue('email', e.target.value, {
                          shouldValidate: true,
                        });
                        setIsDisabled(false);
                      }}
                      error={!!errors.email}
                      disabled
                    />
                  </Col>
                </Row>
                <Row gap="12px">
                  <Col
                    size={hasSecondaryPhone ? 12 : 6}
                    className={styles.phone_container}
                  >
                    <Row gap="8px">
                      <Col size={hasSecondaryPhone ? 6 : 12}>
                        <div className={styles.primary_phone_input_container}>
                          <PhoneInputField
                            required
                            id="phoneNumber"
                            country={countryCode.toLowerCase()}
                            defaultCountry={countryCode.toLowerCase()}
                            isValid
                            value={phoneValue}
                            {...register('phone')}
                            onChange={(value: string) => {
                              if (!value.startsWith('+')) {
                                value = '+' + value;
                              }
                              setValue('phone', value, {
                                shouldValidate: true,
                              });
                              setPhoneVerified(false);
                              setIsDisabled(false);
                              trigger('phone');
                            }}
                            isVerified={phoneVerified}
                            isVerifyDisplay
                            onVerifyClick={() => {
                              setIsOtpModalOpen(true);
                            }}
                            onValidationChange={setIsPhoneValid}
                            dropdownStyle={PHONE_INPUT_STYLE}
                          />
                          <div className={styles.add_phone_container}>
                            {isOtpModalOpen && watch('phone').length > 10 && (
                              <OtpVerification
                                phoneNumber={watch('phone')}
                                setPhoneVerified={setPhoneVerified}
                                setIsOtpModalOpen={setIsOtpModalOpen}
                                setIsDisabled={setIsDisabled}
                              />
                            )}
                            {!hasSecondaryPhone && phoneVerified && (
                              <Typography
                                color="var(--brand-color)"
                                onClick={handleAddPhone}
                                className={styles.cursor_pointer}
                              >
                                +Phone
                              </Typography>
                            )}
                          </div>
                        </div>
                      </Col>
                      {hasSecondaryPhone && (
                        <Col size={6}>
                          <div>
                            <PhoneInputField
                              country={countryCode.toLowerCase()}
                              defaultCountry={countryCode.toLowerCase()}
                              enableAreaCodeStretch
                              international
                              id="phoneNumber2"
                              isValid
                              value={secondaryPhoneValue}
                              onValidationChange={setIsSecondaryPhoneValid}
                              {...register('secondaryPhone')}
                              onChange={(value: string) => {
                                if (!value.startsWith('+')) {
                                  value = '+' + value;
                                }
                                setValue('secondaryPhone', value);
                                if (value === phoneValue) {
                                  setIsDisabled(true);
                                } else {
                                  setIsDisabled(false);
                                }
                              }}
                              dropdownStyle={PHONE_INPUT_STYLE}
                            />
                            <section
                              className={
                                isBothNumbersSame
                                  ? styles.error_with_remove_text
                                  : styles.remove_text
                              }
                            >
                              {isBothNumbersSame && (
                                <Typography
                                  color="var(--error_light)"
                                  fontSize={10}
                                >
                                  Duplicate number
                                </Typography>
                              )}
                              <Typography
                                color="var(--input-error-text-color)"
                                fontSize={10}
                                onClick={() => handleRemovePhone(setValue)}
                              >
                                Remove
                              </Typography>
                            </section>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </Col>

                  <Col size={6}>
                    <div className={styles.organisation_container}>
                      <Input
                        label="Organization Name"
                        placeholder="Enter Organization Name"
                        type="text"
                        value={organizationValue}
                        {...register('organisation', {
                          minLength: {
                            value: 3,
                            message: 'Minimum 3 characters required',
                          },
                          maxLength: {
                            value: 60,
                            message: 'Maximum 60 characters allowed',
                          },
                        })}
                        onChange={e => {
                          setValue('organisation', e?.target?.value, {
                            shouldValidate: true,
                          });
                          setIsDisabled(false);
                        }}
                        error={!!errors.organisation}
                        helperText={
                          errors.organisation?.message &&
                          String(errors.organisation.message)
                        }
                      />
                    </div>
                  </Col>

                  <Col size={6}>
                    <div className={styles.job_tittle_container}>
                      <Select
                        label="Job Title"
                        required={false}
                        optionZIndex={999999}
                        selectedOption={{
                          value: watch('jobTitle'),
                          label: watch('jobTitle'),
                        }}
                        dropDownRef={selectRef}
                        {...register('jobTitle')}
                        className={styles.select_field}
                        optionsList={jobTitleOptions}
                        onChange={option => {
                          setValue('jobTitle', option.value);
                          if (option.value !== 'Other') {
                            setValue('otherJobTitle', '');
                          }
                          setIsDisabled(false);
                        }}
                        onBlur={() => trigger('jobTitle')}
                        errorMsg={
                          errors.jobTitle?.message &&
                          String(errors.jobTitle.message)
                        }
                      />
                    </div>
                  </Col>

                  {watch('jobTitle') === 'Others' && (
                    <Col size={6}>
                      <Input
                        required
                        label="Other Job Title"
                        placeholder="Enter Job Title"
                        type="text"
                        value={watch('otherJobTitle')}
                        {...register('otherJobTitle', {
                          pattern: {
                            value: ALPHABET_WITH_SPACES_ONLY_REGEX,
                            message: 'Only alphabets are allowed',
                          },
                          required: 'Please specify your job title',
                          minLength: {
                            value: 2,
                            message: 'Minimum 2 characters required',
                          },
                          maxLength: {
                            value: 25,
                            message: 'Maximum 25 characters allowed',
                          },
                        })}
                        onChange={e => {
                          setValue('otherJobTitle', e.target.value, {
                            shouldValidate: true,
                          });
                          setIsDisabled(false);
                        }}
                        onBlur={e => {
                          setValue('otherJobTitle', e?.target?.value?.trim(), {
                            shouldValidate: true,
                          });
                        }}
                        error={!!errors.otherJobTitle}
                        helperText={
                          errors.otherJobTitle?.message &&
                          String(errors.otherJobTitle.message)
                        }
                      />
                    </Col>
                  )}
                </Row>
              </Container>
            </div>
          </Drawer>
        );
      }}
    </Form>
  );
};

export default ProfileForm;
